import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { useTranslation } from '../i18n'
import type { EvolutionResponse } from '../types'
import { useTheme } from '../theme'

interface TestEvolutionChartProps {
  data: EvolutionResponse | null
  loading: boolean
}

export function TestEvolutionChart({ data, loading }: TestEvolutionChartProps) {
  const { t } = useTranslation()
  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === 'dark'

  if (loading) {
    return (
      <div className="card" data-testid="test-evolution-chart">
        <h2 className="card-title">{t('component.testEvolution')}</h2>
        <div
          style={{
            height: '300px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--color-gray-400)',
          }}
        >
          {t('label.loading')}
        </div>
      </div>
    )
  }

  if (!data || data.builds.length === 0) {
    return (
      <div className="card" data-testid="test-evolution-chart">
        <h2 className="card-title">{t('component.testEvolution')}</h2>
        <div
          style={{
            height: '300px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--color-gray-400)',
          }}
        >
          {t('empty.noBuilds')}
        </div>
      </div>
    )
  }

  const chartData = data.builds.map((build) => ({
    date: new Date(build.date).toLocaleDateString(),
    passed: build.passed,
    failed: build.failed,
    skipped: build.skipped,
    total: build.total,
  }))

  return (
    <div className="card" data-testid="test-evolution-chart">
      <h2 className="card-title">{t('component.testEvolution')}</h2>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} />
          <XAxis dataKey="date" stroke={isDark ? '#9ca3af' : '#6b7280'} fontSize={12} />
          <YAxis stroke={isDark ? '#9ca3af' : '#6b7280'} fontSize={12} />
          <Tooltip
            contentStyle={{
              backgroundColor: isDark ? '#1f2937' : '#ffffff',
              border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
              borderRadius: '8px',
              color: isDark ? '#f9fafb' : '#111827',
            }}
            labelStyle={{ color: isDark ? '#f9fafb' : '#111827' }}
            itemStyle={{ color: isDark ? '#f9fafb' : '#111827' }}
          />
          <Legend wrapperStyle={{ color: isDark ? '#f9fafb' : '#111827' }} />
          <Line
            type="monotone"
            dataKey="passed"
            stroke="var(--color-success)"
            strokeWidth={2}
            dot={{ fill: 'var(--color-success)' }}
          />
          <Line
            type="monotone"
            dataKey="failed"
            stroke="var(--color-error)"
            strokeWidth={2}
            dot={{ fill: 'var(--color-error)' }}
          />
          <Line
            type="monotone"
            dataKey="skipped"
            stroke="var(--color-warning)"
            strokeWidth={2}
            dot={{ fill: 'var(--color-warning)' }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}
